import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  Heart,
  LogOut,
  Menu,
  Moon,
  Search,
  ShoppingBag,
  Sun,
  User,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useApprox } from './product/Money';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useTheme } from '../contexts/ThemeContext';
import { useDeliveryFacts } from '../contexts/StoreFactsContext';
import { deliveryTimeText, freeDeliveryText } from '../utils/deliveryZones';
import useMediaQuery from '../hooks/useMediaQuery';
import CategoryTabs from './CategoryTabs';
import { MegaMenuFallback } from './MegaMenuTop';
import { languageLabelStyle } from '../utils/systemFont';

// Overlays load on first open, keeping them out of the main bundle.
// Loaded on idle / hover so opening search is instant (no lost keystrokes).
// The desktop mega menu is its own chunk: phones (the nav row is hidden
// below lg) never download it, and the mobile drawer's menu loads on open.
const MegaMenu = lazy(() => import('./MegaMenu'));
// The phone menu drawer: loaded on the first press of the menu button.
const loadMobileMenu = () => import('./MobileMenuDrawer');
const MobileMenuDrawer = lazy(loadMobileMenu);

const loadAdvancedSearch = () => import('./AdvancedSearch');
const AdvancedSearch = lazy(loadAdvancedSearch);
const AuthModal = lazy(() => import('./AuthModal'));
const Wishlist = lazy(() => import('./Wishlist'));
const CurrencyPicker = lazy(() => import('./CurrencyPicker'));

const API = (process.env.REACT_APP_BACKEND_URL || 'https://anum360-store.onrender.com').replace(/\/+$/, '');

// Thin-outline icon buttons, 44px hit area, no disc (LUXURY_UI_SPEC §2 "App bar").
/**
 * A search field that opens the search overlay (the overlay owns typing,
 * suggestions and results). Looks like an input: rounded field, hint text,
 * and a solid ink search button at the end.
 */
function SearchBarButton({ t, onOpen, className = '' }) {
  const warm = () => { loadAdvancedSearch().catch(() => {}); };
  return (
    <button
      type="button"
      onClick={onOpen}
      onPointerEnter={warm}
      onFocus={warm}
      aria-haspopup="dialog"
      aria-label={t({ en: 'Search', ar: 'بحث' })}
      className={`group flex h-11 items-center gap-3 rounded-full border-2 border-charcoal bg-paper ps-4 pe-1 text-start transition-colors hover:border-crimson dark:border-white/80 dark:bg-panel dark:hover:border-white md:h-10 md:rounded-[6px] md:border md:border-rule-strong md:bg-well md:ps-3 md:hover:border-ink ${className}`}
    >
      {/* Desktop: a normal-height field with the glass at the start; phones
          keep the bold pill with the red search button at the end. */}
      <Search size={17} strokeWidth={1.8} className="hidden shrink-0 text-stone md:block" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-[14px] text-stone" aria-hidden="true">
        {t({ en: 'Search bags, jewellery, perfumes', ar: 'ابحث عن الحقائب والمجوهرات والعطور' })}
      </span>
      <span className="flex h-9 w-14 shrink-0 items-center justify-center rounded-full bg-crimson text-white transition-colors group-hover:bg-crimson-deep md:hidden" aria-hidden="true">
        <Search size={19} strokeWidth={2.2} />
      </span>
    </button>
  );
}

const ICON_BTN = 'relative flex h-11 w-11 items-center justify-center text-charcoal transition-colors hover:text-stone dark:text-white dark:hover:text-white/80';
// Desktop (lg): the icon with a small word under it (Account, Wishlist, Bag).
const LABELLED_BTN = `${ICON_BTN} lg:h-12 lg:w-auto lg:min-w-[56px] lg:flex-col lg:gap-0.5 lg:px-2 lg:hover:text-crimson-ink`;
const BTN_WORD = 'hidden text-[12px] font-semibold leading-none lg:block';
// Count badge on the bag and the bell: ink disc, 12 px figures.
const COUNT_BADGE = 'absolute end-0.5 top-0.5 lg:end-2 lg:top-0 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-crimson px-1 text-[14px] font-bold leading-none tabular-nums text-white';

const NotificationBell = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!token) return undefined;

    const loadUnreadCount = async () => {
      try {
        const response = await fetch(`${API}/api/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) return;

        const data = await response.json();
        setCount(Number(data.unread_count || 0));
      } catch {
        // Keep the header responsive even if the notification endpoint is slow.
      }
    };

    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  if (!token) return null;

  return (
    <Link
      to="/account"
      className={ICON_BTN + ' hidden md:flex'}
      aria-label={count
        ? t({ en: `Notifications, ${count} unread`, ar: `الإشعارات، ${count} غير مقروءة` })
        : t({ en: 'Notifications', ar: 'الإشعارات' })}
    >
      <Bell size={20} strokeWidth={1.4} aria-hidden="true" />
      {count > 0 && (
        <span aria-hidden="true" className={COUNT_BADGE}>
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
};

const LuxuryHeader = () => {
  const { language, toggleLanguage, t } = useLanguage();
  // Announcement copy from the delivery-zone table only (no "same-day").
  const deliveryFacts = useDeliveryFacts();
  const approx = useApprox();
  const freeLine = freeDeliveryText(deliveryFacts, approx);
  const timeLine = deliveryTimeText(deliveryFacts);
  const announcement = freeLine || timeLine || { en: 'Authentic luxury, delivered across the UAE', ar: 'فخامة أصيلة، تصلك في أنحاء الإمارات' };
  const announcementWide = freeLine && timeLine
    ? { en: `${freeLine.en} · ${timeLine.en}`, ar: `${freeLine.ar} · ${timeLine.ar}` }
    : announcement;
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // The desktop nav row (lg and up) is the only place the mega menu shows.
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [searchValue, setSearchValue] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);
  const [compact, setCompact] = useState(false);
  const [solidBar, setSolidBar] = useState(false);

  const userMenuRef = useRef(null);
  const sentinelRef = useRef(null);
  const cartCount = cart?.total_items || cart?.items?.length || 0;

  // Frosted compact header: a zero-height sentinel sits where the header
  // starts; once it leaves the viewport the bar compacts (76 → 60 px) and
  // turns frosted. IntersectionObserver, so there is no scroll listener.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof window.IntersectionObserver !== 'function') return undefined;
    const observer = new window.IntersectionObserver(([entry]) => setCompact(!entry.isIntersecting), { threshold: 0 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // --anum-header-h: the header's current height, so sticky bars below it
  // (the shop toolbar) sit flush under it in both sizes.
  const headerRef = useRef(null);
  useEffect(() => {
    const node = headerRef.current;
    if (!node || typeof window.ResizeObserver !== 'function') return undefined;
    // Written only when the height really changes: a custom property on
    // <html> restyles every element of the page.
    let last = '';
    const observer = new window.ResizeObserver(() => {
      const value = `${Math.round(node.getBoundingClientRect().height)}px`;
      if (value === last) return;
      last = value;
      document.documentElement.style.setProperty('--anum-header-h', value);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Blur costs GPU: phones with little memory get a solid bar instead.
  useEffect(() => {
    const lowMemory = typeof navigator !== 'undefined' && Number(navigator.deviceMemory) > 0 && Number(navigator.deviceMemory) < 4;
    setSolidBar(lowMemory && window.innerWidth < 768);
  }, []);

  // Any navigation closes the drawer, including a link that only changes the
  // query (e.g. WOMEN → Abayas while already on /products).
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowUserDropdown(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  useEffect(() => {
    if (!showUserDropdown) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setShowUserDropdown(false);
        userMenuRef.current?.querySelector('button')?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showUserDropdown]);

  // While the overlay is opening (focus not yet in a text field), keep the
  // letters typed so far and hand them to the overlay as its first query.
  useEffect(() => {
    if (!showAdvancedSearch) return undefined;
    const onKey = (event) => {
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;
      if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) return;
      event.preventDefault();
      setSearchValue((value) => value + event.key);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [showAdvancedSearch]);

  const closeAdvancedSearch = () => {
    setShowAdvancedSearch(false);
    setSearchValue('');
  };

  useEffect(() => {
    const openSearch = () => setShowAdvancedSearch(true);
    const openWishlist = () => setShowWishlist(true);
    // "Sign in" on the session-expired notice (AuthContext).
    const openSignIn = (event) => {
      setAuthMode(event?.detail?.mode === 'register' ? 'register' : 'login');
      setShowAuthModal(true);
    };

    window.addEventListener('anum:open-search', openSearch);
    window.addEventListener('anum:open-wishlist', openWishlist);
    window.addEventListener('anum:open-auth', openSignIn);

    return () => {
      window.removeEventListener('anum:open-search', openSearch);
      window.removeEventListener('anum:open-wishlist', openWishlist);
      window.removeEventListener('anum:open-auth', openSignIn);
    };
  }, []);

  // Deep link: any page with ?auth=login|register opens the sign-in dialog in
  // place (used by the bottom nav, Account and Checkout), then drops the param
  // so Back does not reopen it. The shopper stays on the page they came from.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('auth');
    if (mode !== 'login' && mode !== 'register') return;
    setAuthMode(mode);
    setShowAuthModal(true);
    params.delete('auth');
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const openAuth = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setShowUserDropdown(false);
    setMobileMenuOpen(false);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = searchValue.trim();
    if (!trimmed) return;
    navigate(`/products?search=${encodeURIComponent(trimmed)}`);
    setSearchValue('');
    setShowAdvancedSearch(false);
    setMobileMenuOpen(false);
  };

  const quickLinks = [
    { label: { en: 'New in', ar: 'وصل حديثًا' }, path: '/products' },
    { label: { en: 'VIP', ar: 'VIP' }, path: '/vip' },
    { label: { en: 'Gifting', ar: 'الهدايا' }, path: '/corporate-gifting' },
    { label: { en: 'Client care', ar: 'خدمة العملاء' }, path: '/contact' },
  ];

  const cartLabel = cartCount
    ? t({ en: `Shopping bag, ${cartCount} ${cartCount === 1 ? 'item' : 'items'}`, ar: `حقيبة التسوق، ${cartCount} قطع` })
    : t({ en: 'Shopping bag, empty', ar: 'حقيبة التسوق فارغة' });

  return (
    <>
      {/* Announcement band: crimson, white 13 px text (5.9:1). */}
      <div className="bg-crimson text-white" data-surface="dark">
        <div className="luxury-stage-wide flex min-h-[40px] items-center justify-center gap-4 px-4 md:min-h-[36px] md:justify-between">
          <p className="text-center text-[13px] font-semibold leading-snug text-white">
            <span className="md:hidden">{t(announcement)}</span>
            <span className="hidden md:inline">{t(announcementWide)}</span>
          </p>
          <div className="hidden items-center gap-1 md:flex">
            {isDesktop && (
              <Suspense fallback={<span className="inline-block w-12" aria-hidden="true" />}>
                <CurrencyPicker variant="band" />
              </Suspense>
            )}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? t({ en: 'Switch to light mode', ar: 'الوضع الفاتح' }) : t({ en: 'Switch to dark mode', ar: 'الوضع الداكن' })}
              className="flex h-11 w-11 items-center justify-center text-white hover:text-white/80 md:h-9 md:w-9"
            >
              {theme === 'dark' ? <Sun size={15} strokeWidth={1.5} aria-hidden="true" /> : <Moon size={15} strokeWidth={1.5} aria-hidden="true" />}
            </button>
            <button
              type="button"
              onClick={toggleLanguage}
              lang={language === 'en' ? 'ar' : 'en'}
              style={languageLabelStyle(language)}
              aria-label={language === 'en' ? 'التبديل إلى العربية' : 'Switch to English'}
              className="flex h-11 items-center px-2 text-[13px] font-semibold text-white hover:text-white/80 md:h-9"
            >
              {language === 'en' ? 'العربية' : 'English'}
            </button>
          </div>
        </div>
      </div>

      <div ref={sentinelRef} aria-hidden="true" className="h-0" />
      <header
        ref={headerRef}
        data-compact={compact ? 'true' : 'false'}
        data-solid={solidBar ? 'true' : undefined}
        className={`atelier-header sticky top-0 z-[180] border-b transition-colors duration-300 motion-reduce:transition-none ${
          compact ? 'border-rule-strong shadow-[0_10px_28px_-22px_rgb(12_10_9/0.45)]' : 'border-transparent'
        }`}
      >
        <div
          className={`luxury-stage-wide flex items-center gap-2 px-2 transition-[height] duration-300 ease-luxury motion-reduce:transition-none md:gap-6 md:px-4 ${
            compact ? 'h-14 md:h-[56px]' : 'h-16 md:h-[68px]'
          }`}
        >
          {/* Start: menu (mobile) + wordmark */}
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              onPointerDown={() => { loadMobileMenu().catch(() => {}); }}
              className={ICON_BTN + ' lg:hidden'}
              aria-label={t({ en: 'Open menu', ar: 'فتح القائمة' })}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <Menu size={22} strokeWidth={1.4} aria-hidden="true" />
            </button>
            <Link to="/" className="flex flex-col items-start px-1 md:px-2" aria-label={t({ en: 'ANUM 360 Gallery, home', ar: 'ANUM 360 Gallery، الصفحة الرئيسية' })}>
              <span
                className={`latin-wordmark whitespace-nowrap font-playfair text-[1.3rem] font-semibold leading-none tracking-[0.12em] text-charcoal transition-[font-size] duration-300 motion-reduce:transition-none dark:text-white ${
                  compact ? 'md:text-[22px]' : 'md:text-[26px]'
                }`}
                aria-hidden="true"
              >
                ANUM 360
              </span>
              <span
                className={`latin-wordmark mt-1.5 text-[12px] uppercase leading-none tracking-[0.2em] text-stone ${compact ? 'hidden' : 'hidden md:block'}`}
                aria-hidden="true"
              >
                Gallery, Abu Dhabi
              </span>
            </Link>
          </div>

          {/* Middle: search bar (tablet and up; phones get the row below) */}
          <div className="hidden min-w-0 flex-1 justify-center md:flex">
            <SearchBarButton
              t={t}
              onOpen={() => setShowAdvancedSearch(true)}
              className="w-full max-w-[560px]"
            />
          </div>
          <div className="flex-1 md:hidden" />

          {/* End: account, wishlist, notifications, bag */}
          <div className="flex shrink-0 items-center justify-end gap-0.5">
            {compact && (
              <button
                type="button"
                onClick={() => setShowAdvancedSearch(true)}
                className={ICON_BTN + ' md:hidden'}
                aria-haspopup="dialog"
                aria-label={t({ en: 'Search', ar: 'بحث' })}
              >
                <Search size={20} strokeWidth={1.4} aria-hidden="true" />
              </button>
            )}
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setShowUserDropdown((current) => !current)}
                className={LABELLED_BTN}
                aria-label={user ? t({ en: 'Account menu', ar: 'قائمة الحساب' }) : t({ en: 'Sign in or create an account', ar: 'تسجيل الدخول أو إنشاء حساب' })}
                aria-haspopup="true"
                aria-expanded={showUserDropdown}
                aria-controls="account-menu"
              >
                <User size={20} strokeWidth={1.4} aria-hidden="true" />
                <span className={BTN_WORD} aria-hidden="true">{user ? t({ en: 'Account', ar: 'حسابي' }) : t({ en: 'Sign in', ar: 'الدخول' })}</span>
              </button>

              {showUserDropdown && (
                <div id="account-menu" className="absolute end-0 top-[calc(100%+8px)] z-50 w-[300px] border-t-2 border-charcoal bg-paper p-6 shadow-[0_24px_48px_rgba(12,10,9,0.12)] dark:border-rule">
                  {user ? (
                    <>
                      <p className="text-[13px] text-stone">{t({ en: 'Signed in as', ar: 'مسجل باسم' })}</p>
                      <p className="mt-1 truncate font-sans font-extrabold tracking-[-0.01em] text-2xl text-charcoal dark:text-white">{user.full_name || user.name || user.email}</p>
                      <ul className="mt-4 divide-y divide-rule border-t border-rule">
                        <li><Link to="/account" className="flex min-h-[44px] items-center text-[15px] text-charcoal hover:underline hover:underline-offset-4 dark:text-white">{t({ en: 'My account', ar: 'حسابي' })}</Link></li>
                        <li><Link to="/orders" className="flex min-h-[44px] items-center text-[15px] text-charcoal hover:underline hover:underline-offset-4 dark:text-white">{t({ en: 'Orders', ar: 'الطلبات' })}</Link></li>
                        <li>
                          <button type="button" onClick={() => { setShowUserDropdown(false); setShowWishlist(true); }} className="flex min-h-[44px] w-full items-center text-start text-[15px] text-charcoal hover:underline hover:underline-offset-4 dark:text-white">
                            {t({ en: 'Wishlist', ar: 'المفضلة' })}
                          </button>
                        </li>
                        <li>
                          <button type="button" onClick={handleLogout} className="flex min-h-[44px] w-full items-center gap-2 text-start text-[15px] text-charcoal hover:underline hover:underline-offset-4 dark:text-white">
                            <LogOut size={15} strokeWidth={1.5} aria-hidden="true" />
                            {t({ en: 'Sign out', ar: 'تسجيل الخروج' })}
                          </button>
                        </li>
                      </ul>
                    </>
                  ) : (
                    <>
                      <p className="font-sans font-extrabold tracking-[-0.01em] text-2xl text-charcoal dark:text-white">{t({ en: 'Welcome to the maison', ar: 'أهلًا بك' })}</p>
                      <p className="mt-2 text-sm leading-6 text-stone">
                        {t({ en: 'Sign in to keep your wishlist, follow orders and check out faster.', ar: 'سجّل الدخول لحفظ المفضلة ومتابعة الطلبات والدفع أسرع.' })}
                      </p>
                      <div className="mt-4 grid gap-2">
                        <button type="button" onClick={() => openAuth('login')} className="luxury-button w-full">
                          {t({ en: 'Sign in', ar: 'تسجيل الدخول' })}
                        </button>
                        <button type="button" onClick={() => openAuth('register')} className="luxury-button-secondary w-full">
                          {t({ en: 'Create account', ar: 'إنشاء حساب' })}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowWishlist(true)}
              className={LABELLED_BTN + ' hidden md:flex'}
              aria-label={t({ en: 'Wishlist', ar: 'المفضلة' })}
            >
              <Heart size={20} strokeWidth={1.4} aria-hidden="true" />
              <span className={BTN_WORD} aria-hidden="true">{t({ en: 'Wishlist', ar: 'المفضلة' })}</span>
            </button>

            <NotificationBell />

            <Link to="/cart" className={LABELLED_BTN} aria-label={cartLabel}>
              <ShoppingBag size={20} strokeWidth={1.4} aria-hidden="true" />
              <span className={BTN_WORD} aria-hidden="true">{t({ en: 'Bag', ar: 'الحقيبة' })}</span>
              {cartCount > 0 && (
                <span aria-hidden="true" className={COUNT_BADGE}>
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Phones: a full-width search bar under the logo row, like the big
            stores; it folds away once the header compacts on scroll (the
            search icon joins the end icons instead). */}
        {!compact && (
          <div className="px-3 pb-1 md:hidden">
            <SearchBarButton t={t} onOpen={() => setShowAdvancedSearch(true)} className="w-full" />
          </div>
        )}

        {/* Category tabs (phones and tablets; desktop has the mega menu). The
            shop page has its own category bar, so they are not repeated there. */}
        {location.pathname !== '/products' && <CategoryTabs className="lg:hidden" />}

        {/* Category navigation (desktop): all ink, 13 px caps; the house links
            sit apart by space, not by a grey tone or a divider. */}
        <div className="hidden border-t border-rule lg:block">
          <div className="luxury-stage-wide flex h-12 items-center justify-center gap-10 px-4">
            {isDesktop && <Suspense fallback={<MegaMenuFallback />}><MegaMenu /></Suspense>}
            <nav aria-label={t({ en: 'More', ar: 'المزيد' })} className="flex items-center gap-1">
              {quickLinks.slice(1).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                  className="group relative flex min-h-[44px] items-center px-3 text-[15px] font-semibold text-charcoal hover:text-crimson-ink dark:text-white"
                >
                  {t(item.label)}
                  <span aria-hidden="true" className="absolute inset-x-3 bottom-1 h-[3px] origin-center scale-x-0 rounded-full bg-crimson transition-transform duration-200 ease-luxury group-hover:scale-x-100 group-aria-[current=page]:scale-x-100 motion-reduce:transition-none" />
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile menu: modal drawer from the start edge (focus trapped, Esc closes); own chunk. */}
      {mobileMenuOpen && (
        <Suspense fallback={null}>
          <MobileMenuDrawer
            onClose={closeMobileMenu}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onSearchSubmit={handleSearchSubmit}
            quickLinks={quickLinks}
            user={user}
            onLogout={handleLogout}
            onAuth={openAuth}
          />
        </Suspense>
      )}

      <Suspense fallback={null}>
        {showAuthModal && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} initialMode={authMode} />}
        {showAdvancedSearch && <AdvancedSearch isOpen={showAdvancedSearch} onClose={closeAdvancedSearch} initialQuery={searchValue} />}
        {showWishlist && <Wishlist isOpen={showWishlist} onClose={() => setShowWishlist(false)} />}
      </Suspense>
    </>
  );
};

export default LuxuryHeader;
